/**
 * OpenAIRealtimeClient — WebRTC-based client for OpenAI Realtime API.
 *
 * The main OPENAI_API_KEY NEVER touches this code — the browser talks to
 * OpenAI directly using a short-lived ephemeral client_secret that our
 * backend minted for this session. If the key were exposed here, it would
 * be visible in devtools; we deliberately go through the backend for the
 * ephemeral mint.
 *
 * Event mapping vs. OpenAI Realtime server events:
 *   session.created                       → connected
 *   input_audio_buffer.speech_started     → listening
 *   input_audio_buffer.speech_stopped     → thinking
 *   conversation.item.input_audio_transcription.completed → transcript(user, final)
 *   response.audio_transcript.delta       → transcript(assistant, partial)
 *   response.audio_transcript.done        → transcript(assistant, final)
 *   response.done                         → state(ready) + accumulate usage
 *   response.function_call_arguments.done → resolve via backend tool endpoint
 *   response.audio.delta                  → outbound audio (played by remote track)
 *   response.cancelled                    → interruption
 *   error                                 → error({recoverable})
 */
import { VoiceClient } from "./VoiceClient";

export class OpenAIRealtimeClient extends VoiceClient {
  constructor({ backendBaseUrl, toolResolvers }) {
    super();
    this.backendBaseUrl = backendBaseUrl.replace(/\/$/, "");
    this.toolResolvers = toolResolvers || {};
    this.pc = null;
    this.dc = null;
    this.localStream = null;
    this.remoteAudio = null;
    this.sessionKey = null;
    this.sessionId = null;
    this.tools = [];
    this._muted = false;
    this._ended = false;
    this._transcript = [];       // client-side captured turns for post-session summary
    this._pendingAsst = null;
    this._usage = {
      audioInputSeconds: 0, audioOutputSeconds: 0,
      inputTokens: 0, outputTokens: 0,
      interruptions: 0, userTurns: 0,
      languageDetected: null,
    };
    this._speechStartAt = null;
    this._responseAudioStartAt = null;
  }

  async connect({ sessionKey, consent, pageContext, voice, sourcePage }) {
    if (!consent) throw new Error("Consent required");
    this.sessionKey = sessionKey;
    this.emit("state", { state: "connecting" });
    this.emit("connecting");

    // 1) mint ephemeral session on backend
    const mintRes = await fetch(`${this.backendBaseUrl}/api/voice/session`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        session_key: sessionKey,
        consent_accepted: true,
        voice,
        source_page: sourcePage,
        page_context: pageContext,
      }),
    });
    if (!mintRes.ok) {
      const err = await mintRes.json().catch(() => ({}));
      throw new Error(err.detail || "Voice service unavailable.");
    }
    const mint = await mintRes.json();
    this.sessionId = mint.session_id;
    this.tools = mint.tools || [];
    const ephemeralSecret = mint.client_secret?.value;
    const webrtcUrl = mint.metadata?.webrtc_url || "https://api.openai.com/v1/realtime";
    const model = mint.model;

    // 2) create PeerConnection
    this.pc = new RTCPeerConnection();

    // 3) remote audio playback
    this.remoteAudio = document.createElement("audio");
    this.remoteAudio.autoplay = true;
    this.remoteAudio.playsInline = true;
    this.pc.ontrack = (event) => {
      this.remoteAudio.srcObject = event.streams[0];
    };

    // 4) local mic
    this.localStream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true, noiseSuppression: true, autoGainControl: true,
        sampleRate: 24000, channelCount: 1,
      },
    });
    this.localStream.getTracks().forEach((t) => this.pc.addTrack(t, this.localStream));

    // 5) data channel for events
    this.dc = this.pc.createDataChannel("oai-events");
    this.dc.onmessage = (ev) => this._handleServerEvent(ev.data);
    this.dc.onopen = () => this.emit("connected");

    // 6) SDP negotiation
    const offer = await this.pc.createOffer();
    await this.pc.setLocalDescription(offer);

    const sdpRes = await fetch(`${webrtcUrl}?model=${encodeURIComponent(model)}`, {
      method: "POST",
      body: offer.sdp,
      headers: {
        Authorization: `Bearer ${ephemeralSecret}`,
        "Content-Type": "application/sdp",
      },
    });
    if (!sdpRes.ok) {
      throw new Error("Voice service handshake failed.");
    }
    const answerSdp = await sdpRes.text();
    await this.pc.setRemoteDescription({ type: "answer", sdp: answerSdp });

    this.emit("state", { state: "ready" });
  }

  _handleServerEvent(raw) {
    let ev;
    try { ev = typeof raw === "string" ? JSON.parse(raw) : raw; } catch { return; }
    const t = ev.type;

    switch (t) {
      case "session.created":
      case "session.updated":
        this.emit("connected");
        break;
      case "input_audio_buffer.speech_started":
        this._speechStartAt = Date.now();
        this.emit("state", { state: "listening" });
        this.emit("listening");
        break;
      case "input_audio_buffer.speech_stopped":
        if (this._speechStartAt) {
          this._usage.audioInputSeconds += (Date.now() - this._speechStartAt) / 1000;
          this._speechStartAt = null;
        }
        this.emit("state", { state: "thinking" });
        this.emit("thinking");
        break;
      case "conversation.item.input_audio_transcription.completed":
        this._usage.userTurns += 1;
        if (ev.transcript) this._transcript.push({ role: "user", text: ev.transcript });
        this.emit("transcript", { role: "user", text: ev.transcript || "", final: true });
        break;
      case "response.audio_transcript.delta":
        if (this._responseAudioStartAt == null) this._responseAudioStartAt = Date.now();
        this._pendingAsst = (this._pendingAsst || "") + (ev.delta || "");
        this.emit("state", { state: "speaking" });
        this.emit("transcript", { role: "assistant", text: ev.delta || "", final: false });
        break;
      case "response.audio_transcript.done":
        if (ev.transcript || this._pendingAsst) {
          this._transcript.push({ role: "assistant", text: ev.transcript || this._pendingAsst });
        }
        this._pendingAsst = null;
        this.emit("transcript", { role: "assistant", text: ev.transcript || "", final: true });
        break;
      case "response.audio.done":
      case "response.done":
        if (this._responseAudioStartAt) {
          this._usage.audioOutputSeconds += (Date.now() - this._responseAudioStartAt) / 1000;
          this._responseAudioStartAt = null;
        }
        if (ev.response?.usage) {
          this._usage.inputTokens += ev.response.usage.input_tokens || 0;
          this._usage.outputTokens += ev.response.usage.output_tokens || 0;
        }
        this.emit("state", { state: "ready" });
        break;
      case "response.function_call_arguments.done":
        this._handleToolCall(ev);
        break;
      case "response.cancelled":
        this._usage.interruptions += 1;
        this.emit("interruption");
        this.emit("state", { state: "listening" });
        break;
      case "error":
        this.emit("error", { message: ev.error?.message || "Voice error", recoverable: true });
        break;
      default:
        // silently ignore other events
        break;
    }
  }

  async _handleToolCall(ev) {
    // ev fields: call_id, name, arguments (stringified JSON)
    const name = ev.name;
    let args = {};
    try { args = JSON.parse(ev.arguments || "{}"); } catch { args = {}; }
    const resolver = this.toolResolvers[name];
    let output;
    try {
      output = resolver
        ? await resolver({ ...args, session_key: this.sessionKey })
        : { error: "tool_not_implemented", name };
    } catch (e) {
      output = { error: "tool_failed", message: String(e?.message || e).slice(0, 200) };
    }
    this.emit("toolcall", { name, args, result: output });

    // Reply to the model
    this._send({
      type: "conversation.item.create",
      item: {
        type: "function_call_output",
        call_id: ev.call_id,
        output: JSON.stringify(output).slice(0, 6000),
      },
    });
    // Ask the model to continue with the tool result
    this._send({ type: "response.create" });
  }

  _send(obj) {
    if (this.dc && this.dc.readyState === "open") {
      this.dc.send(JSON.stringify(obj));
    }
  }

  async mute() {
    if (!this.localStream) return;
    this.localStream.getAudioTracks().forEach((t) => (t.enabled = false));
    this._muted = true;
  }
  async unmute() {
    if (!this.localStream) return;
    this.localStream.getAudioTracks().forEach((t) => (t.enabled = true));
    this._muted = false;
  }
  isMuted() { return this._muted; }

  async sendText(text) {
    if (!text) return;
    this._send({
      type: "conversation.item.create",
      item: { type: "message", role: "user", content: [{ type: "input_text", text }] },
    });
    this._send({ type: "response.create" });
  }

  async end() {
    if (this._ended) return;
    this._ended = true;
    try { this.dc?.close(); } catch { /* ignore */ }
    try { this.pc?.close(); } catch { /* ignore */ }
    try { this.localStream?.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
    if (this.remoteAudio) {
      try { this.remoteAudio.pause(); } catch { /* ignore */ }
      try { this.remoteAudio.remove(); } catch { /* ignore */ }
    }
    // Send end/usage to backend (best-effort)
    try {
      await fetch(`${this.backendBaseUrl}/api/voice/end`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_key: this.sessionKey,
          end_reason: "user_ended",
          audio_input_seconds: this._usage.audioInputSeconds,
          audio_output_seconds: this._usage.audioOutputSeconds,
          input_tokens: this._usage.inputTokens,
          output_tokens: this._usage.outputTokens,
          interruption_count: this._usage.interruptions,
          user_turn_count: this._usage.userTurns,
          language_detected: this._usage.languageDetected,
          transcript: this._transcript.slice(-100),
        }),
      });
    } catch { /* ignore */ }
    this.emit("ended", { reason: "user_ended", usage: this._usage });
  }

  getUsage() { return { ...this._usage }; }
}
